import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import './RegisterPage.scss';

function safeRedirect(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
  return raw;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get('redirect'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = (email || '').trim().toLowerCase();
      const response = await authAPI.register(normalizedEmail, password);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate(redirectTo);
    } catch (err: any) {
      if (!err.response) {
        setError(t('register.errors.serverUnavailable'));
      } else {
        setError(err.response?.data?.error || t('register.errors.registrationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-page__container">
        <button type="button" className="page-back" onClick={() => navigate('/')} aria-label={t('nav.back')}>
          ← {t('nav.back')}
        </button>
        <header className="register-page__header">
          <h1 className="register-page__title">{t('register.title')}</h1>
        </header>
        <form className="register-page__form" onSubmit={handleSubmit}>
          {error && <div className="form-error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('register.form.email')}
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('register.form.password')}
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              {t('register.form.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('register.form.loading') : t('register.form.submit')}
          </button>
        </form>
        <p className="register-page__legal">
          {t('register.legalPrefix')}{' '}
          <Link to="/terms-of-service" className="link">{t('home.footer.termsOfService')}</Link>
          {' · '}
          <Link to="/terms" className="link">{t('home.footer.privacy')}</Link>
        </p>
        <p className="register-page__footer">
          {t('register.hasAccount')}{' '}
          <Link to={`/login?redirect=${encodeURIComponent(redirectTo)}`} className="link">
            {t('register.login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
